import {
  Button,
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@light-technology/components";
import { XClose } from "@untitledui/icons";
import { useState } from "react";
import {
  useFormState,
  type Control,
  type UseFormHandleSubmit,
} from "react-hook-form";
import { useNavigate } from "react-router";
import { api } from "../../state/api";
import type { AppResponse, PlanDetailResponse } from "../../state/api.types";
import { useFormatPlanName } from "../../utilities/useFormatPlanName";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { ChangeDetails } from "./ChangeDetails";
import { useFormValues, type FormValues } from "./common";
import type { RenewalPlanOption } from "./renewalPlan";

const FIELD_MAP: Partial<
  Record<
    keyof FormValues,
    { apiField: string; transform: (v: unknown) => unknown }
  >
> = {
  name: { apiField: "name", transform: (v) => v },
  description: { apiField: "description", transform: (v) => v },
  termMonths: {
    apiField: "term_months",
    transform: (v) => parseInt(v as string),
  },
  baseFee: {
    apiField: "plan_monthly_cost_dollars",
    transform: (v) => parseFloat(v as string),
  },
  monthlyCommission: {
    apiField: "monthly_commission_dollars",
    transform: (v) => (v ? parseFloat(v as string) : null),
  },
  exportRate: {
    apiField: "export_rate_kwh_dollars",
    transform: (v) => parseFloat(v as string) / 100,
  },
  renewalPlanUuid: { apiField: "renewal_plan_uuid", transform: (v) => v },
};

type PublishButtonProps = {
  app: AppResponse;
  plan: PlanDetailResponse;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  control: Control<FormValues>;
  renewalPlanOptions: RenewalPlanOption[];
};

export const PublishButton = ({
  app,
  plan,
  handleSubmit,
  control,
  renewalPlanOptions,
}: PublishButtonProps) => {
  const formState = useFormState({ control });
  const isDirty = formState.isDirty;
  const formValues = useFormValues(control);
  const navigate = useNavigate();
  const formatPlanName = useFormatPlanName();

  const [isOpen, setIsOpen] = useState(false);
  const [updatePlan, updatePlanState] = useMutationWithToast(
    api.useUpdatePlanMutation(),
    {
      successMessage: `The ${formatPlanName(formValues.name.field.value)} changes have been saved.`,
      onSuccess: () => {
        navigate(`/apps/${app.slug}/plans/`);
      },
    },
  );
  const onPublish = handleSubmit((values: FormValues) => {
    const dirtyPayload = (
      Object.keys(formState.dirtyFields) as (keyof FormValues)[]
    ).reduce<Record<string, unknown>>((acc, key) => {
      const mapping = FIELD_MAP[key];
      if (mapping) acc[mapping.apiField] = mapping.transform(values[key]);
      return acc;
    }, {});
    updatePlan({ planUUID: plan.uuid, ...dirtyPayload });
  });

  const tryOpenDialog = handleSubmit(() => {
    if (!isDirty) {
      return;
    }
    setIsOpen(true);
  });

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) {
          setIsOpen(false);
          return;
        }

        if (updatePlanState.isLoading) {
          return;
        }
        tryOpenDialog();
      }}
    >
      <Button
        size="md"
        isDisabled={updatePlanState.isLoading || !isDirty}
        data-ph-capture-attribute-action="publish-plan-changes"
      >
        Review changes
      </Button>
      <ModalOverlay isDismissable>
        <Modal>
          <Dialog>
            <div className="flex min-w-100 flex-col gap-4xl rounded-2xl bg-primary p-3xl">
              <div className="flex flex-col">
                <div className="flex flex-row items-center justify-between">
                  <div className="text-md font-semibold text-primary">
                    Do the changes look correct?
                  </div>
                  <XClose
                    className="cursor-pointer text-quaternary"
                    onClick={() => {
                      setIsOpen(false);
                    }}
                  />
                </div>
              </div>
              <ChangeDetails
                plan={plan}
                control={control}
                renewalPlanOptions={renewalPlanOptions}
              />
              <div className="flex flex-row gap-3xl">
                <Button
                  size="md"
                  color="secondary"
                  className="w-1/2"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  className="w-1/2"
                  isDisabled={updatePlanState.isLoading}
                  onClick={() => {
                    onPublish();
                    setIsOpen(false);
                  }}
                  data-ph-capture-attribute-action="confirm-publish-plan"
                >
                  Yes, publish
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
